import type {
  ExecutionJobChangedEvent,
  ExecutionJobState,
} from "@magic-md/engine-client/protocol";
import type { EngineService } from "../platform";
import {
  type EngineOperationLease,
  StaleEngineOperationError,
} from "./engine-operation-controller";
import type { NotificationBus } from "./notification-bus";

export const DEFAULT_EXECUTION_JOB_POLL_INTERVAL_MS = 500;
export const DEFAULT_EXECUTION_JOB_EVENT_WATCHDOG_MS = 5_000;

export interface ExecutionJobWaiterDependencies {
  getEngine: () => Pick<EngineService, "executionGetState">;
  getNotificationBus: () => Pick<NotificationBus, "connected" | "subscribe">;
  pollIntervalMs?: number;
  eventWatchdogMs?: number;
}

function waitForLeaseDelay(
  ms: number,
  lease: EngineOperationLease,
): Promise<void> {
  lease.assertCurrent();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      lease.signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new StaleEngineOperationError());
    };
    lease.signal.addEventListener("abort", onAbort, { once: true });
    if (!lease.isCurrent()) onAbort();
  });
}

export function waitForEnginePromise<T>(
  promise: Promise<T>,
  lease: EngineOperationLease,
): Promise<T> {
  lease.assertCurrent();
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      lease.signal.removeEventListener("abort", onAbort);
    };
    const onAbort = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new StaleEngineOperationError());
    };
    lease.signal.addEventListener("abort", onAbort, { once: true });
    if (!lease.isCurrent()) {
      onAbort();
      return;
    }
    promise.then(
      (value) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(value);
      },
      (error: unknown) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      },
    );
  });
}

export function isTerminalExecutionJob(job: ExecutionJobState): boolean {
  return (
    job.status === "completed" ||
    job.status === "failed" ||
    job.status === "cancelled"
  );
}

/**
 * Waits primarily on jobChanged notifications and polls only when no
 * notification transport is available or a watchdog detects a missed event.
 */
export class ExecutionJobWaiter {
  private readonly pollIntervalMs: number;
  private readonly eventWatchdogMs: number;

  constructor(private readonly dependencies: ExecutionJobWaiterDependencies) {
    this.pollIntervalMs =
      dependencies.pollIntervalMs ?? DEFAULT_EXECUTION_JOB_POLL_INTERVAL_MS;
    this.eventWatchdogMs =
      dependencies.eventWatchdogMs ?? DEFAULT_EXECUTION_JOB_EVENT_WATCHDOG_MS;
  }

  async wait(
    executionId: string,
    lease: EngineOperationLease,
  ): Promise<ExecutionJobState> {
    lease.assertCurrent();
    const engine = this.dependencies.getEngine();
    const bus = this.dependencies.getNotificationBus();
    let latestNotification: ExecutionJobChangedEvent | null = null;
    let notificationVersion = 0;
    let wake: ((reason: "notification" | "watchdog") => void) | null = null;
    const unsubscribe = bus.connected
      ? bus.subscribe<ExecutionJobChangedEvent>(
          "event/execution.jobChanged",
          (event) => {
            if (!lease.isCurrent()) return;
            if (event.executionId !== executionId) return;
            latestNotification = event;
            notificationVersion += 1;
            wake?.("notification");
          },
        )
      : null;

    try {
      let shouldPoll = true;
      for (;;) {
        lease.assertCurrent();
        if (latestNotification && isTerminalExecutionJob(latestNotification)) {
          return latestNotification;
        }

        if (shouldPoll) {
          const job = await waitForEnginePromise(
            engine.executionGetState({ executionId }),
            lease,
          );
          lease.assertCurrent();
          if (isTerminalExecutionJob(job)) return job;
          if (
            latestNotification &&
            isTerminalExecutionJob(latestNotification)
          ) {
            return latestNotification;
          }
        }

        if (!unsubscribe || !bus.connected) {
          await waitForLeaseDelay(this.pollIntervalMs, lease);
          shouldPoll = true;
          continue;
        }

        const observedVersion = notificationVersion;
        const wakeReason = await new Promise<"notification" | "watchdog">(
          (resolve, reject) => {
            let settled = false;
            const cleanup = () => {
              clearTimeout(timer);
              lease.signal.removeEventListener("abort", onAbort);
            };
            const finish = (reason: "notification" | "watchdog") => {
              if (settled) return;
              settled = true;
              cleanup();
              if (wake === finish) wake = null;
              resolve(reason);
            };
            const onAbort = () => {
              if (settled) return;
              settled = true;
              cleanup();
              if (wake === finish) wake = null;
              reject(new StaleEngineOperationError());
            };
            const timer = setTimeout(
              () => finish("watchdog"),
              this.eventWatchdogMs,
            );
            wake = finish;
            lease.signal.addEventListener("abort", onAbort, {
              once: true,
            });
            if (!lease.isCurrent()) {
              onAbort();
              return;
            }
            if (notificationVersion !== observedVersion) {
              finish("notification");
            }
          },
        );
        shouldPoll = wakeReason === "watchdog";
      }
    } finally {
      unsubscribe?.();
      wake = null;
    }
  }
}
